import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useApi, useIncremental } from "@/lib/cache";
import { fmtDur } from "@/lib/format";
import { LoadState, ListSentinel } from "@/components/LoadState";
import { ArtworkPlaceholder } from "@/components/ArtworkPlaceholder";
import { ChevronDown, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { EditItemDialog } from "@/components/EditItemDialog";

export default function Proyectos() {
  const { data, loading, error, reload: load } = useApi("/projects");
  const projects = data || [];
  const { visible, sentinelRef, hasMore } = useIncremental(projects);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const toggle = async (id) => {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next && !detail[id]) {
      const { data } = await api.get(`/projects/${id}`);
      setDetail((d) => ({ ...d, [id]: data }));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto animate-in">
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Projects</h1>
        <p className="text-sm text-zinc-500 mt-1">{projects.length} in total</p>
      </header>

      {loading || error ? (
        <LoadState loading={loading} error={error} onRetry={load} testid="projects" />
      ) : projects.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-lg text-zinc-300 mb-2">No projects yet.</p>
          <p className="text-sm text-zinc-500">Create an EP or an album from + New.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((p) => {
            const d = detail[p.id];
            return (
              <div key={p.id} className="rounded-xl border border-white/5 bg-[#141416] overflow-hidden" data-testid={`project-row-${p.id}`}>
                <div className="flex items-center gap-3 p-4 hover:bg-white/[0.02]">
                  <button onClick={() => toggle(p.id)} className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center"
                    data-testid={`project-expand-${p.id}`}>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${expanded === p.id ? "rotate-180" : ""}`} />
                  </button>
                  <Link to={`/projects/${p.id}`} className="flex items-center gap-3 flex-1 min-w-0" data-testid={`project-open-${p.id}`}>
                    <ArtworkPlaceholder title={p.name} artworkId={p.artwork_id} size="md" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider" data-testid={`project-meta-${p.id}`}>{p.type === "ep" ? "EP" : "Album"} · {p.track_count || 0} song{p.track_count === 1 ? "" : "s"} · {fmtDur(p.total_duration)}</div>
                    </div>
                  </Link>
                  {isOwner && (
                    <button onClick={() => setEditing(p)} data-testid={`project-edit-${p.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white hover:bg-white/5">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {expanded === p.id && d && (
                  <div className="p-4 pt-0 space-y-1">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500" style={{ width: `${d.progress}%` }} />
                      </div>
                      <span className="mono text-[10px] text-violet-300 tabular">{Math.round(d.progress)}%</span>
                    </div>
                    {d.tracks.length === 0 ? (
                      <p className="text-xs text-zinc-500 italic">No songs yet.</p>
                    ) : d.tracks.map((t, i) => (
                      <Link key={t.id} to={`/songs/${t.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.03]">
                        <span className="mono text-xs text-zinc-500 w-6">{String(i + 1).padStart(2, "0")}</span>
                        <span className="text-sm flex-1 truncate">{t.name}</span>
                        <div className="h-1 w-20 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full bg-violet-500" style={{ width: `${t.progress}%` }} />
                        </div>
                        <span className="mono text-[10px] text-zinc-500 w-8 text-right">{Math.round(t.progress)}%</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <ListSentinel hasMore={hasMore} sentinelRef={sentinelRef} />

      <EditItemDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} kind="project" item={editing} onSaved={load} />
    </div>
  );
}
